import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  RefreshCw, Users, Settings, ExternalLink, Link2, CheckCircle, 
  XCircle, Clock, Shield, UserPlus, Key, Send, Globe, Database,
  Activity, AlertCircle, Loader2, Copy, Eye, EyeOff
} from 'lucide-react';
import { 
  InfinityAlchemyIcon, LotusIcon, SanctuaryIcon, StarburstIcon 
} from './icons/AlchemyIcons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Platform configurations
const PLATFORMS = {
  elchme: {
    name: 'ElchmE',
    fullName: 'ElchmE Research Portal',
    url: 'https://elchme.com',
    role: 'Research & Soul Scores',
    color: 'purple',
    icon: InfinityAlchemyIcon,
    ssoSecret: 'belonging_sessions_shared_secret_2024',
    webhookKey: 'J8TI3s6zsGBUyOkO7iqsdbK8wU0PE46I',
    sourceHeader: 'elchme',
    description: 'Soul Score assessments, research activities, vision boards'
  },
  mairy: {
    name: 'm.ai.ry',
    fullName: 'm.ai.ry Wisdom Bot',
    url: 'https://mairy.io',
    role: 'Wisdom & Insights',
    color: 'pink',
    icon: LotusIcon,
    ssoSecret: 'belonging_sessions_mairy_secret_2024',
    webhookKey: 'bWAO73qH1emTy0CoT3rdwOsk5vy5awlu',
    sourceHeader: 'mairy',
    description: 'AI wisdom conversations, insights, reflections'
  },
  blissabode: {
    name: 'BlissAbode',
    fullName: 'BlissAbode Wellness Sanctuary',
    url: 'https://blissabode.org',
    role: 'Wellness & Self-Care',
    color: 'rose',
    icon: SanctuaryIcon,
    ssoSecret: 'belonging_sessions_blissabode_secret_2024',
    webhookKey: 'bLiSsAbOdE_w3bH00k_K3y_2024_s3cur3',
    sourceHeader: 'blissabode',
    description: 'Meditation, wellness scores, self-care activities'
  }
};

export default function SisterPlatformManager() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [platformStatus, setPlatformStatus] = useState(null);
  const [crossPlatformUsers, setCrossPlatformUsers] = useState([]);
  const [platformAdmins, setPlatformAdmins] = useState({
    elchme: [],
    mairy: [],
    blissabode: []
  });
  const [selectedPlatform, setSelectedPlatform] = useState('elchme');
  const [showSecrets, setShowSecrets] = useState({});
  const [testingSSO, setTestingSSO] = useState({});
  const [syncingData, setSyncingData] = useState({});
  
  // Admin action states
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  // Fetch platform connectivity status
  const fetchPlatformStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/platform-connectivity`);
      if (response.ok) {
        const data = await response.json();
        setPlatformStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch platform status:', error);
      toast.error('Failed to fetch platform status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch cross-platform user data
  const fetchCrossPlatformUsers = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/cross-platform-users`);
      if (response.ok) {
        const data = await response.json();
        setCrossPlatformUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch cross-platform users:', error);
    }
  }, []);

  // Fetch platform admins
  const fetchPlatformAdmins = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/platform-admins`);
      if (response.ok) {
        const data = await response.json();
        setPlatformAdmins(data.admins || { elchme: [], mairy: [], blissabode: [] });
      }
    } catch (error) {
      console.error('Failed to fetch platform admins:', error);
    }
  }, []);

  useEffect(() => {
    fetchPlatformStatus();
    fetchCrossPlatformUsers();
    fetchPlatformAdmins();
  }, [fetchPlatformStatus, fetchCrossPlatformUsers, fetchPlatformAdmins]);

  // Test SSO connection
  const testSSOConnection = async (platform) => {
    setTestingSSO(prev => ({ ...prev, [platform]: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/sso/validate/test`);
      if (response.ok) {
        const data = await response.json();
        if (data[`${platform}_configured`]) {
          toast.success(`${PLATFORMS[platform].name} SSO is configured and ready!`);
        } else {
          toast.warning(`${PLATFORMS[platform].name} SSO needs configuration`);
        }
      }
    } catch (error) {
      toast.error(`Failed to test ${PLATFORMS[platform].name} SSO`);
    } finally {
      setTestingSSO(prev => ({ ...prev, [platform]: false }));
    }
  };

  // Sync platform data
  const syncPlatformData = async (platform) => {
    setSyncingData(prev => ({ ...prev, [platform]: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sync-platform/${platform}`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success(`${PLATFORMS[platform].name} data sync initiated!`);
        fetchPlatformStatus();
      } else {
        toast.error(`Failed to sync ${PLATFORMS[platform].name} data`);
      }
    } catch (error) {
      toast.error(`Sync error: ${error.message}`);
    } finally {
      setSyncingData(prev => ({ ...prev, [platform]: false }));
    }
  };

  // Add platform admin
  const addPlatformAdmin = async (platform) => {
    if (!newAdminEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/platform-admins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform, email: newAdminEmail })
      });
      
      if (response.ok) {
        toast.success(`Admin added to ${PLATFORMS[platform].name}`);
        setNewAdminEmail('');
        fetchPlatformAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add admin');
      }
    } catch (error) {
      toast.error('Failed to add admin');
    }
  };

  // Remove platform admin
  const removePlatformAdmin = async (platform, email) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/platform-admins/${platform}/${encodeURIComponent(email)}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success(`Admin removed from ${PLATFORMS[platform].name}`);
        fetchPlatformAdmins();
      }
    } catch (error) {
      toast.error('Failed to remove admin');
    }
  };

  // Send broadcast to all platforms
  const sendBroadcast = async () => {
    if (!broadcastMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingBroadcast(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: broadcastMessage, platforms: Object.keys(PLATFORMS) })
      });
      
      if (response.ok) {
        toast.success('Broadcast sent to all platforms!');
        setBroadcastMessage('');
      } else {
        toast.error('Failed to send broadcast');
      }
    } catch (error) {
      toast.error('Broadcast error');
    } finally {
      setSendingBroadcast(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // Toggle secret visibility
  const toggleSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'bg-emerald-500';
      case 'active': return 'bg-emerald-500';
      case 'configured': return 'bg-amber-500';
      case 'pending': return 'bg-stone-500';
      default: return 'bg-stone-600';
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      connected: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      active: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      configured: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      pending: 'bg-stone-500/20 text-stone-300 border-stone-500/30'
    };
    return colors[status] || colors.pending;
  };

  return (
    <div className="space-y-6" data-testid="sister-platform-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            Sister Platform Management
          </h2>
          <p className="text-stone-400 text-sm mt-1">
            Manage interconnected platforms: ElchmE, m.ai.ry, and BlissAbode
          </p>
        </div>
        <Button
          onClick={fetchPlatformStatus}
          disabled={loading}
          variant="outline"
          className="border-stone-700 text-stone-300 hover:bg-stone-800"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Status
        </Button>
      </div>

      {/* Platform Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(PLATFORMS).map(([key, platform]) => {
          const IconComponent = platform.icon;
          const status = platformStatus?.platforms?.[key]?.status || 'pending';
          const stats = platformStatus?.platforms?.[key]?.stats || {};
          
          return (
            <Card 
              key={key}
              className={`bg-stone-900/50 border-stone-700 hover:border-${platform.color}-500/50 transition-all cursor-pointer ${selectedPlatform === key ? `border-${platform.color}-500` : ''}`}
              onClick={() => setSelectedPlatform(key)}
              data-testid={`platform-card-${key}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg bg-${platform.color}-500/20 flex items-center justify-center`}>
                    <IconComponent className={`w-6 h-6 text-${platform.color}-400`} />
                  </div>
                  <Badge className={getStatusBadge(status)}>
                    {status}
                  </Badge>
                </div>
                
                <h3 className="text-lg font-semibold text-white mb-1">{platform.name}</h3>
                <p className="text-sm text-stone-400 mb-3">{platform.role}</p>
                
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {key === 'elchme' && (
                    <>
                      <div className="bg-stone-800/50 rounded p-2">
                        <span className="text-stone-500">Activities</span>
                        <p className="text-white font-medium">{stats.total_activities || 0}</p>
                      </div>
                      <div className="bg-stone-800/50 rounded p-2">
                        <span className="text-stone-500">Soul Scores</span>
                        <p className="text-white font-medium">{stats.soul_scores || 0}</p>
                      </div>
                    </>
                  )}
                  {key === 'mairy' && (
                    <>
                      <div className="bg-stone-800/50 rounded p-2">
                        <span className="text-stone-500">Conversations</span>
                        <p className="text-white font-medium">{stats.total_conversations || 0}</p>
                      </div>
                      <div className="bg-stone-800/50 rounded p-2">
                        <span className="text-stone-500">Insights</span>
                        <p className="text-white font-medium">{stats.total_insights || 0}</p>
                      </div>
                    </>
                  )}
                  {key === 'blissabode' && (
                    <>
                      <div className="bg-stone-800/50 rounded p-2">
                        <span className="text-stone-500">Activities</span>
                        <p className="text-white font-medium">{stats.total_activities || 0}</p>
                      </div>
                      <div className="bg-stone-800/50 rounded p-2">
                        <span className="text-stone-500">Synced 24h</span>
                        <p className="text-white font-medium">{stats.synced_last_24h || 0}</p>
                      </div>
                    </>
                  )}
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-xs"
                    onClick={(e) => { e.stopPropagation(); testSSOConnection(key); }}
                    disabled={testingSSO[key]}
                  >
                    {testingSSO[key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3 mr-1" />}
                    Test SSO
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-xs"
                    onClick={(e) => { e.stopPropagation(); syncPlatformData(key); }}
                    disabled={syncingData[key]}
                  >
                    {syncingData[key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Sync
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs"
                    onClick={(e) => { e.stopPropagation(); window.open(platform.url, '_blank'); }}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-stone-900 border border-stone-700">
          <TabsTrigger value="overview" className="data-[state=active]:bg-stone-700">
            <Globe className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-stone-700">
            <Users className="w-4 h-4 mr-2" />
            Cross-Platform Users
          </TabsTrigger>
          <TabsTrigger value="admins" className="data-[state=active]:bg-stone-700">
            <Shield className="w-4 h-4 mr-2" />
            Admin Accounts
          </TabsTrigger>
          <TabsTrigger value="api" className="data-[state=active]:bg-stone-700">
            <Key className="w-4 h-4 mr-2" />
            API & Credentials
          </TabsTrigger>
          <TabsTrigger value="actions" className="data-[state=active]:bg-stone-700">
            <Settings className="w-4 h-4 mr-2" />
            Master Controls
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Connection Status */}
            <Card className="bg-stone-900/50 border-stone-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5 text-emerald-400" />
                  Connection Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(PLATFORMS).map(([key, platform]) => {
                  const status = platformStatus?.platforms?.[key]?.status || 'pending';
                  const ssoConfigured = platformStatus?.platforms?.[key]?.sso_configured;
                  const webhookConfigured = platformStatus?.platforms?.[key]?.webhook_configured;
                  
                  return (
                    <div key={key} className="flex items-center justify-between p-3 bg-stone-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${getStatusColor(status)}`} />
                        <span className="text-white font-medium">{platform.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={ssoConfigured ? 'border-emerald-500 text-emerald-400' : 'border-stone-600 text-stone-500'}>
                          SSO {ssoConfigured ? '✓' : '○'}
                        </Badge>
                        <Badge variant="outline" className={webhookConfigured ? 'border-emerald-500 text-emerald-400' : 'border-stone-600 text-stone-500'}>
                          Webhook {webhookConfigured ? '✓' : '○'}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Database Summary */}
            <Card className="bg-stone-900/50 border-stone-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Database className="w-5 h-5 text-amber-400" />
                  Shared Database
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-stone-800/50 rounded-lg">
                    <span className="text-stone-400">Status</span>
                    <Badge className="bg-emerald-500/20 text-emerald-300">
                      {platformStatus?.database?.status || 'Connected'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-stone-800/50 rounded-lg">
                    <span className="text-stone-400">Total Synced Items</span>
                    <span className="text-white font-medium">
                      {platformStatus?.summary?.total_synced_items || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-stone-800/50 rounded-lg">
                    <span className="text-stone-400">Synced Last 24h</span>
                    <span className="text-white font-medium">
                      {platformStatus?.summary?.synced_last_24h || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-stone-800/50 rounded-lg">
                    <span className="text-stone-400">Active Platforms</span>
                    <span className="text-white font-medium">
                      {platformStatus?.summary?.active_platforms || 0} / {platformStatus?.summary?.total_platforms || 4}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Cross-Platform Users Tab */}
        <TabsContent value="users" className="mt-4">
          <Card className="bg-stone-900/50 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">Cross-Platform User Accounts</CardTitle>
              <p className="text-stone-400 text-sm">Users with activity across multiple sister platforms</p>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-stone-700">
                    <TableHead className="text-stone-400">User</TableHead>
                    <TableHead className="text-stone-400">Email</TableHead>
                    <TableHead className="text-stone-400 text-center">Belonging</TableHead>
                    <TableHead className="text-stone-400 text-center">ElchmE</TableHead>
                    <TableHead className="text-stone-400 text-center">m.ai.ry</TableHead>
                    <TableHead className="text-stone-400 text-center">BlissAbode</TableHead>
                    <TableHead className="text-stone-400">Last Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {crossPlatformUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-stone-500 py-8">
                        No cross-platform users found yet. Users will appear here when they use multiple platforms.
                      </TableCell>
                    </TableRow>
                  ) : (
                    crossPlatformUsers.map((user) => (
                      <TableRow key={user.id} className="border-stone-800">
                        <TableCell className="text-white font-medium">{user.name}</TableCell>
                        <TableCell className="text-stone-400">{user.email}</TableCell>
                        <TableCell className="text-center">
                          {user.platforms?.belonging ? (
                            <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" />
                          ) : (
                            <XCircle className="w-4 h-4 text-stone-600 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {user.platforms?.elchme ? (
                            <CheckCircle className="w-4 h-4 text-purple-400 mx-auto" />
                          ) : (
                            <XCircle className="w-4 h-4 text-stone-600 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {user.platforms?.mairy ? (
                            <CheckCircle className="w-4 h-4 text-pink-400 mx-auto" />
                          ) : (
                            <XCircle className="w-4 h-4 text-stone-600 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {user.platforms?.blissabode ? (
                            <CheckCircle className="w-4 h-4 text-rose-400 mx-auto" />
                          ) : (
                            <XCircle className="w-4 h-4 text-stone-600 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-stone-500 text-sm">
                          {user.last_active ? new Date(user.last_active).toLocaleDateString() : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Accounts Tab */}
        <TabsContent value="admins" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {Object.entries(PLATFORMS).map(([key, platform]) => {
              const IconComponent = platform.icon;
              const admins = platformAdmins[key] || [];
              
              return (
                <Card key={key} className="bg-stone-900/50 border-stone-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <IconComponent className={`w-5 h-5 text-${platform.color}-400`} />
                      {platform.name} Admins
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {admins.length === 0 ? (
                      <p className="text-stone-500 text-sm text-center py-4">
                        No admins configured yet
                      </p>
                    ) : (
                      admins.map((admin, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-stone-800/50 rounded">
                          <div>
                            <p className="text-white text-sm">{admin.name || admin.email}</p>
                            <p className="text-stone-500 text-xs">{admin.email}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => removePlatformAdmin(key, admin.email)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))
                    )}
                    
                    <div className="pt-3 border-t border-stone-700">
                      <div className="flex gap-2">
                        <Input
                          placeholder="admin@email.com"
                          value={selectedPlatform === key ? newAdminEmail : ''}
                          onChange={(e) => {
                            setSelectedPlatform(key);
                            setNewAdminEmail(e.target.value);
                          }}
                          className="bg-stone-800 border-stone-600 text-white text-sm"
                        />
                        <Button
                          size="sm"
                          onClick={() => addPlatformAdmin(key)}
                          className={`bg-${platform.color}-600 hover:bg-${platform.color}-500`}
                        >
                          <UserPlus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* API & Credentials Tab */}
        <TabsContent value="api" className="mt-4">
          <Card className="bg-stone-900/50 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-400" />
                API Credentials & Integration Keys
              </CardTitle>
              <p className="text-stone-400 text-sm">
                Copy these credentials to integrate sister platforms with Belonging Sessions
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(PLATFORMS).map(([key, platform]) => {
                const IconComponent = platform.icon;
                
                return (
                  <div key={key} className="p-4 bg-stone-800/50 rounded-lg border border-stone-700">
                    <div className="flex items-center gap-3 mb-4">
                      <IconComponent className={`w-6 h-6 text-${platform.color}-400`} />
                      <h4 className="text-white font-medium">{platform.fullName}</h4>
                      <a 
                        href={platform.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-stone-500 hover:text-stone-300"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* SSO Secret */}
                      <div>
                        <Label className="text-stone-400 text-xs">SSO Secret</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type={showSecrets[`${key}-sso`] ? 'text' : 'password'}
                            value={platform.ssoSecret}
                            readOnly
                            className="bg-stone-900 border-stone-600 text-white font-mono text-xs"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleSecret(`${key}-sso`)}
                          >
                            {showSecrets[`${key}-sso`] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(platform.ssoSecret, 'SSO Secret')}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Webhook Key */}
                      <div>
                        <Label className="text-stone-400 text-xs">Webhook Key</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type={showSecrets[`${key}-webhook`] ? 'text' : 'password'}
                            value={platform.webhookKey}
                            readOnly
                            className="bg-stone-900 border-stone-600 text-white font-mono text-xs"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleSecret(`${key}-webhook`)}
                          >
                            {showSecrets[`${key}-webhook`] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(platform.webhookKey, 'Webhook Key')}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Source Header */}
                      <div>
                        <Label className="text-stone-400 text-xs">Source Header Value</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            value={`X-Source-App: ${platform.sourceHeader}`}
                            readOnly
                            className="bg-stone-900 border-stone-600 text-white font-mono text-xs"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(`X-Source-App: ${platform.sourceHeader}`, 'Source Header')}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Validation Endpoint */}
                      <div>
                        <Label className="text-stone-400 text-xs">SSO Validation Endpoint</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            value={`${BACKEND_URL}/api/sso/validate`}
                            readOnly
                            className="bg-stone-900 border-stone-600 text-white font-mono text-xs"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(`${BACKEND_URL}/api/sso/validate`, 'Endpoint')}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Documentation Link */}
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                  <div>
                    <p className="text-amber-200 font-medium">Full API Documentation</p>
                    <p className="text-amber-200/70 text-sm mt-1">
                      See <code className="bg-amber-900/50 px-1 rounded">/app/SISTER_PLATFORM_SSO_API.md</code> for complete integration guide with code examples.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Master Controls Tab */}
        <TabsContent value="actions" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Broadcast Message */}
            <Card className="bg-stone-900/50 border-stone-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Send className="w-5 h-5 text-blue-400" />
                  Broadcast to All Platforms
                </CardTitle>
                <p className="text-stone-400 text-sm">
                  Send a notification to all users across all sister platforms
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Enter your broadcast message..."
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  className="bg-stone-800 border-stone-600 text-white min-h-[100px]"
                />
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {Object.entries(PLATFORMS).map(([key, platform]) => {
                      const IconComponent = platform.icon;
                      return (
                        <Badge key={key} variant="outline" className="border-stone-600">
                          <IconComponent className={`w-3 h-3 mr-1 text-${platform.color}-400`} />
                          {platform.name}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
                <Button
                  onClick={sendBroadcast}
                  disabled={sendingBroadcast || !broadcastMessage.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-500"
                >
                  {sendingBroadcast ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Broadcast
                </Button>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-stone-900/50 border-stone-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-amber-400" />
                  Master Admin Actions
                </CardTitle>
                <p className="text-stone-400 text-sm">
                  Remote administrative controls for sister platforms
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start border-stone-700 text-white hover:bg-stone-800"
                  onClick={() => {
                    Object.keys(PLATFORMS).forEach(p => syncPlatformData(p));
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sync All Platforms
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start border-stone-700 text-white hover:bg-stone-800"
                  onClick={() => {
                    Object.keys(PLATFORMS).forEach(p => testSSOConnection(p));
                  }}
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Test All SSO Connections
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start border-stone-700 text-white hover:bg-stone-800"
                  onClick={fetchCrossPlatformUsers}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Refresh User Data
                </Button>
                
                <div className="pt-3 border-t border-stone-700">
                  <p className="text-stone-500 text-xs mb-2">Platform-Specific Actions</p>
                  {Object.entries(PLATFORMS).map(([key, platform]) => {
                    const IconComponent = platform.icon;
                    return (
                      <Dialog key={key}>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            className="w-full justify-start text-stone-300 hover:bg-stone-800 mb-1"
                          >
                            <IconComponent className={`w-4 h-4 mr-2 text-${platform.color}-400`} />
                            Manage {platform.name}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-md">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                              <IconComponent className={`w-5 h-5 text-${platform.color}-400`} />
                              {platform.fullName} Controls
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-3 mt-4">
                            <Button
                              variant="outline"
                              className="w-full justify-start border-stone-700"
                              onClick={() => window.open(platform.url, '_blank')}
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Open {platform.name} Site
                            </Button>
                            <Button
                              variant="outline"
                              className="w-full justify-start border-stone-700"
                              onClick={() => syncPlatformData(key)}
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Force Data Sync
                            </Button>
                            <Button
                              variant="outline"
                              className="w-full justify-start border-stone-700"
                              onClick={() => testSSOConnection(key)}
                            >
                              <Shield className="w-4 h-4 mr-2" />
                              Test SSO Connection
                            </Button>
                            <p className="text-stone-500 text-xs pt-2">
                              {platform.description}
                            </p>
                          </div>
                        </DialogContent>
                      </Dialog>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
